import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DatePicker } from '@/components/common/DatePicker';
import SubmitButton from '@/components/common/SubmitButton';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import { Switch } from '@/components/shadcn/switch';
import { Textarea } from '@/components/shadcn/textarea';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import type { Party } from '@/features/party/types';
import useAuth from '@/hooks/useAuth';

import useActionsDias from '../../hooks/useActionsDias';
import useEnsureEmailDocument from '../../hooks/useEnsureEmailDocument';
import useDiasEmployees from '../../queries/hooks/useDiasEmployees';
import AssigneeField from '../AssigneeField';
import PriorityField from '../PriorityField';

import type { ExportEmailValuesDias } from './types';

type Props = ModalProps & {
  defaultValues?: Partial<ExportEmailValuesDias>;
  party: Maybe<Party>;
};

const CRM_KEY = 'dias';

export default function ExportEmailModalDias({
  onClose,
  defaultValues,
  party,
}: Props) {
  const { t } = useTranslation();

  const { integrations } = useAuth();
  const ensureEmailDocument = useEnsureEmailDocument();
  const { data: employees } = useDiasEmployees();

  const brokerDiasEmployee = employees?.find(
    (employee) => employee.value === integrations?.dias_preferred_agent_nr,
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    watch,
  } = useForm<ExportEmailValuesDias>({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      createTask: true,
      title: defaultValues?.title,
      description: defaultValues?.description,
      date: defaultValues?.date ? new Date(defaultValues.date) : new Date(),
      priority: defaultValues?.priority ?? 0,
      assignee: brokerDiasEmployee,
    },
  });

  const crmPartyId = party?.external_refs.find(
    (ref) => ref.company.toLowerCase() === CRM_KEY,
  )?.ref;

  const diasActions = useActionsDias();
  const { getSelectedMessageId } = useEmailClient();

  const onSuccess = () => {
    posthog.capture('Email exported to CRM', {
      crmBackend: CRM_KEY,
      partyId: party?.id,
      messageId: getSelectedMessageId(),
      withTask: Boolean(createTask),
    });
    toast.success(t('EMAIL_EXPORT_SUCCESS'));

    onClose();
  };

  const onError = (error: Error) => {
    toast.error(t('EMAIL_EXPORT_ERROR'));
    Sentry.captureException(error);
  };

  const onSubmit: SubmitHandler<ExportEmailValuesDias> = async (data) => {
    try {
      if (!crmPartyId) {
        throw new Error('Missing crmPartyId when exporting to dias');
      }

      const { filename, link } = await ensureEmailDocument();

      if (createTask) {
        await diasActions.createTask(
          {
            title: data.title,
            description: data.description,
            priority: data.priority,
            dueDate: data.date ? format(data.date, 'yyyy-MM-dd') : undefined,
            assignedTo: data.assignee?.value,
            crmPartyId,
            documents: [
              {
                filename,
                link,
                description: data.description,
              },
            ],
          },
          {
            onSuccess,
            onError,
          },
        );
      } else {
        await diasActions.uploadDocuments(
          {
            crmPartyId,
            documents: [
              {
                description: data.title,
                filename,
                link,
              },
            ],
          },
          { onSuccess, onError },
        );
      }

      onClose();
    } catch (error) {
      toast.error(t('EMAIL_EXPORT_ERROR'));
      Sentry.captureException(error);
    }
  };

  const createTask = watch('createTask');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <div className="mb-3">
          <Controller
            control={control}
            name="createTask"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Switch
                  name="createTask"
                  id="createTask"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />

                <FieldLabel htmlFor="createTask" className="cursor-pointer">
                  {t('EXPORT_EMAIL_CREATE_TASK_LABEL')}
                </FieldLabel>
              </Field>
            )}
          />
        </div>

        <Field>
          <FieldLabel htmlFor="title">{t('TASK_FIELD_TITLE')}</FieldLabel>
          <Input
            {...register('title', { required: true })}
            placeholder={t('TASK_FIELD_TITLE_PH')}
          />
        </Field>

        {createTask && (
          <>
            <Field>
              <FieldLabel htmlFor="description">
                {t('TASK_FIELD_DESCRIPTION')}
              </FieldLabel>
              <Textarea
                {...register('description', { required: createTask })}
                placeholder={t('TASK_FIELD_DESCRIPTION_PH')}
                className="h-48"
              />
            </Field>

            <PriorityField<ExportEmailValuesDias>
              control={control}
              priorities={[
                { label: t('PRIORITY_DIAS_0'), value: 0 },
                { label: t('PRIORITY_DIAS_1'), value: 1 },
                { label: t('PRIORITY_DIAS_2'), value: 2 },
                { label: t('PRIORITY_DIAS_3'), value: 3 },
              ]}
            />

            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('TASK_FIELD_DUE_DATE')}</FieldLabel>
                  <DatePicker {...field} />
                </Field>
              )}
            />

            <AssigneeField
              control={control}
              employees={employees}
              required={createTask}
            />
          </>
        )}
      </div>

      <div className="flex justify-end">
        <SubmitButton isSubmitting={isSubmitting} disabled={!isValid}>
          {createTask
            ? t('EXPORT_EMAIL_AND_CREATE_TASK_SUBMIT_BUTTON')
            : t('EXPORT_EMAIL_SUBMIT_BUTTON')}
        </SubmitButton>
      </div>
    </form>
  );
}
